import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faArrowUpRight } from "@fortawesome/pro-solid-svg-icons";

import { TicketLinkedEntityType } from "@signalist/shared-types";

import { Icon } from "@/components";
import { ReportProblemButton } from "@/components/SupportTicket/ReportProblemButton";

import { useGetLeadRelationshipQuery } from "@/pages/Private/redux/list/list-relationship.api";
import { useConnectedCrmProviders } from "@/pages/Private/pages/Integrations/hooks/useConnectedCrmProviders";
import { toAddToCrmListOption } from "@/pages/Private/components/AddToCrm/addToCrmListOptions";
import { useAddToCrmModal } from "@/pages/Private/components/AddToCrm/useAddToCrmModal";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import {
	isDummyProspect,
	isPlaceholderProspectName,
} from "@/pages/Private/pages/List/utils/columns/dummyProspect";

import { CampaignElsewhereNotice } from "./CampaignElsewhereNotice";
import { CampaignPausedNotice } from "./CampaignPausedNotice";
import { PauseOutreachButton } from "./PauseOutreachButton";
import { ReachOutActionButton } from "./ReachOutActionButton";
import { SelectProspectActionButton } from "./SelectProspectActionButton";
import { AddToMenu } from "./AddToMenu";
import { useLeadPanelActions } from "./useLeadPanelActions";
import { canComposeReachOut, ComposeReachOutSource } from "./composeReachOut.utils";
import {
	needsCampaignLookup,
	resolveCampaignActions,
	resolveCampaignLocationLink,
} from "./resolveCampaignActions";
import {
	deriveLeadMembership,
	describeReachOut,
	MembershipCountRecord,
	MembershipCrmRecord,
} from "./leadPanelActions.utils";

interface LeadPanelActionsProps {
	prospectId: number;
	prospectName: string;
	firstName: string;
	/** Resolved full `ListPerson` for the lead; `null` when the lead is on no list. */
	listPerson: ListPerson | null;
	listPersons?: MembershipCountRecord[] | null;
	trackerPersons?: MembershipCountRecord[] | null;
	crmProspects?: MembershipCrmRecord[] | null;
	/** Enter the panel's Chat view (the reach-out drafting experience). */
	onReachOut: () => void;
	/**
	 * Whether {@link onReachOut} leads anywhere on this surface. `false` means the primary reach-out
	 * button is **not rendered** — never rendered dead: a control the surface can never enable says
	 * nothing to the user and hides the action that state does permit (PREC-4107). Surfaces without
	 * a chat of their own (the Messages Inbox/ReadyToSend/Compose rails, whose composer *is* the
	 * middle column) pass `false`; the Outbox passes `true` and routes the click to its Chat tab.
	 * Defaults to `true`.
	 */
	reachOutEnabled?: boolean;
	/**
	 * Whether anything campaign-aware may render — "Pause outreach", the resume notice, and the
	 * notice for a campaign running on another list. Defaults to `true`, and the Messages surfaces
	 * keep it: a lead being mailed from the Outbox is exactly the lead a user wants to stop.
	 */
	campaignActionsEnabled?: boolean;
	/**
	 * Whether the Inbox/Outbox deep link is worth offering. The Messages surfaces pass `false` — the
	 * lead is already in the view it would link to. Deliberately separate from
	 * {@link campaignActionsEnabled}: one flag for both is what suppressed the pause button on the
	 * only screen that needed it. Defaults to `true`.
	 */
	campaignLocationLinkEnabled?: boolean;
	portalContainer?: HTMLElement | null;
	/** Web-app origin used to build the Inbox/Outbox deep link from the Chrome extension. */
	webAppUrl?: string;
	/**
	 * Identity of the lead, used to add them to the Compose list when they are on no list yet
	 * (PREC-3825). Without either of these the reach-out button keeps its old disabled state.
	 */
	prospectLinkedinUrl?: string | null;
	prospectCompanyName?: string | null;
	/**
	 * Receives the `ListPerson` created by that Compose add, so the host panel can point its Chat
	 * view at it. Hosts that omit it keep the disabled-with-hint behaviour.
	 */
	onComposeListPersonCreated?: (listPersonId: number) => void;
}

/**
 * The person action-buttons row under the identity card (mockup `lp-person-actions`). One row with
 * a variable primary slot, so the label and the action are decided together (PREC-4107):
 *
 * | slot | content |
 * |---|---|
 * | primary | placeholder lead → "Select prospect"; else outreach queued → "Pause outreach"; else the surface can reach out → "Reach out / Review drafts / Reply"; else nothing |
 * | secondary | the Inbox/Outbox deep link where there is one, otherwise the "Add to ▾" menu |
 * | always | "Report a problem", plus the resume notice below when the outreach is held |
 *
 * A placeholder ("SELECT" / "Person not found") lead has no contact to reach out to. Campaign and
 * webhook require the lead to already be on a list; when it isn't they are disabled with a hint.
 * Reach-out is not (PREC-3825): for a list-less lead the button adds them to the Compose list on
 * click and only then enters the Chat view.
 */
export const LeadPanelActions: FunctionComponent<LeadPanelActionsProps> = ({
	prospectId,
	prospectName,
	firstName,
	listPerson,
	listPersons,
	trackerPersons,
	crmProspects,
	onReachOut,
	reachOutEnabled = true,
	campaignActionsEnabled = true,
	campaignLocationLinkEnabled = true,
	portalContainer,
	webAppUrl,
	prospectLinkedinUrl,
	prospectCompanyName,
	onComposeListPersonCreated,
}) => {
	const { t } = useTranslation();

	const actions = useLeadPanelActions({
		prospectId,
		prospectName,
		listPersonId: listPerson?.id,
		listPerson,
		onComposeListPersonCreated,
	});
	const connectedCrmProviders = useConnectedCrmProviders();
	const crmModal = useAddToCrmModal({
		selectedCount: 1,
		isLoading: actions.isAddingToCrm,
		currentList: toAddToCrmListOption(listPerson),
		onSubmit: actions.addToCrm,
	});

	const membership = deriveLeadMembership({ listPersons, trackerPersons, crmProspects });
	// "Report a problem" (PREC-3938 decision 5): a lead on a list links its ListPerson row; a
	// list-less lead only names the person in the subject.
	const reportProblemPrefill = {
		subject: t("support.reportProblemSubject.lead", { name: prospectName }),
		linkedEntityLabel: prospectName,
		...(listPerson
			? { linkedEntity: { type: TicketLinkedEntityType.LEAD, id: listPerson.id } }
			: {}),
	};
	// Who is *actually* campaigning this prospect — resolved prospect-wide, so it may name a row on
	// a different list than the one this panel was opened on (PREC-3906). Skipped on surfaces that
	// suppress campaign actions anyway, so they cost no request.
	const { data: relationship } = useGetLeadRelationshipQuery(
		{ listPersonId: listPerson?.id ?? 0 },
		{ skip: !needsCampaignLookup(listPerson, campaignActionsEnabled) }
	);
	const campaignPlan = resolveCampaignActions({
		listPerson,
		activeCampaign: relationship?.data.activeCampaign,
		prospectName,
		enabled: campaignActionsEnabled,
	});
	const locationLink = resolveCampaignLocationLink({
		plan: campaignPlan,
		listPerson,
		enabled: campaignActionsEnabled,
		locationLinkEnabled: campaignLocationLinkEnabled,
	});
	// A placeholder ("SELECT" / "Person not found") lead has no real contact to reach out to — the
	// primary action becomes selecting the prospect instead. Until the row loads, fall back to the
	// prospect name so a placeholder never flashes the reach-out button.
	const isPlaceholderLead = listPerson
		? isDummyProspect(listPerson)
		: isPlaceholderProspectName(prospectName);

	// A lead on no list has no `ListPerson`, so the Chat view has nothing to open on. Instead of
	// disabling the button (PREC-3825) we add them to the company's hidden Compose list first and
	// enter the Chat view on the `ListPerson` that add creates.
	const composeSource: ComposeReachOutSource = {
		linkedinUrl: prospectLinkedinUrl,
		name: prospectName,
		companyName: prospectCompanyName,
	};
	const canCompose =
		!listPerson && !!onComposeListPersonCreated && canComposeReachOut(composeSource);

	const enterReachOut = async () => {
		if (listPerson || (await actions.startComposeReachOut(composeSource))) {
			onReachOut();
		}
	};

	/**
	 * The primary slot. Every arm is something the surface can actually carry out — a state the row
	 * cannot act on renders nothing rather than a dead button.
	 */
	const renderPrimaryAction = () => {
		if (isPlaceholderLead) {
			return <SelectProspectActionButton listPerson={listPerson} />;
		}

		if (campaignPlan.kind === "campaign") {
			return (
				<PauseOutreachButton
					loading={actions.isPausing}
					onPause={() => void actions.pauseCampaign(campaignPlan.pauseListPersonId)}
				/>
			);
		}

		if (!reachOutEnabled) {
			return null;
		}

		return (
			<ReachOutActionButton
				descriptor={describeReachOut(listPerson)}
				disabled={!listPerson && !canCompose}
				disabledHint={!listPerson ? t("contacts.leadPanel.actions.addTo.needsListHint") : undefined}
				firstName={firstName}
				loading={actions.isComposing}
				onClick={() => void enterReachOut()}
			/>
		);
	};

	/**
	 * The deep link where the surface offers one, the "Add to ▾" menu otherwise — the row has space
	 * for one of them beside the primary action, and a lead already shown in its destination view
	 * has no use for a link back to it.
	 */
	const renderSecondaryAction = () =>
		locationLink ? (
			<a
				className="flex h-8 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-[10px] border border-gray-200 bg-white px-2.5 text-xs font-semibold text-text duration-200 hover:shadow-md"
				data-testid="lead-panel-location-campaign"
				href={`${webAppUrl ?? ""}${locationLink.to}`}
				rel="noreferrer"
				target="_blank"
			>
				{t(
					`contacts.leadPanel.actions.campaign.${locationLink.kind === "INBOX" ? "inbox" : "outbox"}`
				)}
				<Icon className="w-3" icon={faArrowUpRight} />
			</a>
		) : (
			<AddToMenu
				actions={actions}
				connectedCrmProviders={connectedCrmProviders}
				listPerson={listPerson}
				membership={membership}
				portalContainer={portalContainer}
				onAddToCrm={crmModal.openAddToCrm}
			/>
		);

	return (
		// Aligns with the person/company cards (both `mx-3`); the person card's `mb-2` supplies the
		// 8px gap above (mockup `lp-person-actions` margin-top), and `mb-3` opens a gap below.
		<div className="mx-3 mb-3" data-testid="lead-panel-actions">
			<div className="flex gap-1.5">
				{renderPrimaryAction()}
				{renderSecondaryAction()}
				<ReportProblemButton prefill={reportProblemPrefill} />
			</div>
			{campaignPlan.kind === "paused" && (
				<CampaignPausedNotice
					loading={actions.isResuming}
					onResume={() => void actions.resumeCampaign(campaignPlan.resumeListPersonId)}
				/>
			)}
			{campaignPlan.kind === "elsewhere" && (
				<CampaignElsewhereNotice
					campaignName={campaignPlan.campaignName}
					listName={campaignPlan.listName}
					to={campaignPlan.to}
					webAppUrl={webAppUrl}
				/>
			)}
			{crmModal.renderAddToCrmModal()}
			{actions.renderSnackbar()}
		</div>
	);
};
